import { z } from "zod";
import { defineStepConfig } from "../../registry";
import { conditionalSocket } from "../../types";

const inputSchema = z
  .object({
    approverEmails: z
      .array(z.string())
      .optional()
      .describe(
        "Email addresses of approvers to notify. Required when notifyApprovers is enabled in config.",
      ),
    draftSalesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "ID of the draft sales order under review. Used when inputType is 'sales_order'; rendered as a rich artifact in the approver's inbox.",
      ),
    // Discounts v2: the alternate approval subject. Exactly one of
    // draftSalesOrderId / discountGroupId should be wired; existing sales-order
    // workflows never pass this, so the branch is inert for them.
    discountGroupId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "ID of the discount group under review. Used when inputType is 'discount_group'; decisions are recorded on the group and routed via the group sockets.",
      ),
    // Procurement PO approval: the alternate approval subject. Existing
    // sales-order / discount-group workflows never pass this, so the branch is
    // inert for them.
    draftPurchaseOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "ID of the draft purchase order under review. Used when inputType is 'draft_purchase_order'; decisions are recorded in the approval-decision store and routed via the PO sockets.",
      ),
  })
  .passthrough();

const outputSchema = z.object({
  // Widened additively: existing workflows branch on approved/rejected and keep
  // matching; timed_out/escalated are new terminal decisions for team flows.
  // "pending" is the event_driven terminal: the step created a task + ended the
  // run without taking any routable socket; it resumes via a fresh run when the
  // task is completed (TASK_COMPLETED re-trigger).
  decision: z.enum([
    "approved",
    "rejected",
    "timed_out",
    "escalated",
    "pending",
  ]),
  decidedBy: z.string().uuid().nullable(),
  decidedAt: z.string(),
  comment: z.string().nullable(),
  // Echoes the sales-order subject (from input.draftSalesOrderId) ONLY when the
  // decision is "approved", so an approved draft can route straight to the
  // submit step. On reject/timeout/escalate the socket is not taken, and skip
  // propagation stops the approved-path downstream steps for that item.
  approvedDraftSalesOrderId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The approved draft sales order id, forwarded to downstream submit only when the decision is approved",
    ),
  // Mirror of approvedDraftSalesOrderId for the reject outcome: taken only when
  // the decision is "rejected", so the canvas can route rejected drafts (e.g.
  // back to a fix/re-evaluate breakpoint) without affecting the approved path.
  rejected: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The rejected draft sales order id, taken only when the decision is rejected",
    ),
  // Discount-group subject mirrors of the two sockets above: taken only when
  // the subject is a discount group (input.discountGroupId) AND the decision
  // matches, so the canvas can route approve → activate / reject → mark
  // rejected without touching the sales-order paths.
  approvedDiscountGroupId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The approved discount group id, taken only when the decision is approved",
    ),
  rejectedDiscountGroupId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The rejected discount group id, taken only when the decision is rejected",
    ),
  // Draft-purchase-order subject mirrors of the two sockets above: taken only
  // when the subject is a draft PO (input.draftPurchaseOrderId) AND the decision
  // matches, so the canvas can route approve → release / reject → hold without
  // touching the sales-order or discount-group paths.
  approvedDraftPurchaseOrderId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The approved draft purchase order id, taken only when the decision is approved",
    ),
  rejectedDraftPurchaseOrderId: conditionalSocket(z.string().uuid())
    .optional()
    .describe(
      "The rejected draft purchase order id, taken only when the decision is rejected",
    ),
});

/** A single field/operator/value condition (rule-evaluator compatible). */
const ApprovalConditionSchema = z.object({
  field: z.string(),
  operator: z.enum([
    "eq",
    "neq",
    "gt",
    "gte",
    "lt",
    "lte",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_null",
    "is_not_null",
    "in",
    "not_in",
    "regex_match",
  ]),
  value: z.unknown().optional(),
});

/** One condition group — ALL conditions must match (AND). */
const ApprovalConditionGroupSchema = z.object({
  conditions: z.array(ApprovalConditionSchema),
});

const configSchema = z
  .object({
    inputType: z
      .enum([
        "document",
        "text",
        "sales_order",
        "discount_group",
        "draft_purchase_order",
      ])
      .default("document")
      .describe("The type of input the approver will review"),
    // Execution model:
    //  - "blocking" (default): the step pauses the run on a Temporal signal until a
    //    decision arrives (back-compatible; used by every existing workflow).
    //  - "event_driven": the step does NOT pause. If a decision already exists for
    //    (subject, approvalKey) it routes on it; otherwise it raises one task,
    //    notifies once, and ENDS the run. Completing the task records the decision
    //    and emits TASK_COMPLETED, which re-triggers the (event-driven) workflow to
    //    re-read the decision and continue. For the OMS v3 approval-chain workflow.
    executionMode: z
      .enum(["blocking", "event_driven"])
      .default("blocking")
      .describe(
        "blocking = pause/Temporal-wait for a decision; event_driven = create a task + end the run, resume via a fresh run on TASK_COMPLETED.",
      ),
    // Stable key identifying THIS approval gate's decision on the subject draft, so
    // a fresh event_driven run reads the prior decision. Defaults to the step id.
    approvalKey: z
      .string()
      .optional()
      .describe(
        "Stable key for this approval's decision on the subject (event_driven mode). Defaults to the step id.",
      ),
    // OR of groups, AND within a group (mirrors the Filter Rules builder). When
    // absent/empty the step always applies; when present and the order's facts
    // match no group, the step auto-passes (emits the approved subject) without
    // notifying or waiting — so a chain of conditional Approval steps lets each
    // gate apply only to the orders it targets.
    appliesWhen: z
      .array(ApprovalConditionGroupSchema)
      .optional()
      .describe(
        "Condition groups (OR of groups, AND within) over the order facts; the step only requires approval when the facts match. Empty/absent = always applies.",
      ),
    // Initiator self-approval (draft_purchase_order gates only for now): the
    // subject creator's act of creating it counts as THEIR approval when they
    // could have decided this gate themselves — an ASSIGNED approver (direct
    // team member or named user; out-of-office delegates deliberately do NOT
    // inherit this) who holds the domain's RBAC approve grant. Replaces the
    // per-gate `initiatorGroupIds not_contains <team>` filter rules, which
    // duplicated the gate's team ids into its rules. Assignment-based on
    // purpose: the RBAC grant alone is decide-sufficient regardless of team
    // (see canDecideWorkflowApproval), so a grant-only check would let a
    // junior tier's creator skip every senior gate.
    allowInitiatorSelfApproval: z
      .boolean()
      .default(false)
      .describe(
        "When true (draft_purchase_order gates only), the gate auto-approves as the subject's creator when they are one of this gate's assigned approvers (direct team member or named user) and hold the RBAC approve grant — creating the subject counts as their approval.",
      ),
    // Each named escalation path adds an output socket the approver can route to
    // (e.g. 'vp_review'), letting the canvas wire a follow-up Approval step. An
    // optional per-path `appliesWhen` condition lets a path AUTO-route on the
    // order's facts (e.g. total >= 100k → 'exec_review') without manual selection.
    escalationPaths: z
      .array(
        z.object({
          name: z
            .string()
            .regex(
              /^[a-z][a-z0-9_]*$/,
              "Escalation path name must be a valid socket id (lowercase, underscores)",
            ),
          label: z.string().optional(),
          appliesWhen: z
            .array(ApprovalConditionGroupSchema)
            .optional()
            .describe(
              "Condition groups (OR of groups, AND within) over the order facts; when exactly one path's condition matches, the step auto-escalates to it. Paths without a condition are manual-only.",
            ),
        }),
      )
      .optional()
      .describe(
        "Named escalation outcomes; each adds an output socket the approver can route to (manually) or auto-route to (via appliesWhen).",
      ),
    // THE approver shape. One polymorphic list of principals — teams and users
    // side by side — OR-joined: any entry may decide (still ONE decision per
    // gate; the list widens who is authorized + notified, not how many
    // decisions are required). Each entry carries its own notification mode
    // (hierarchical): "email" → email + in-app; "in_app" → in-app only; "none"
    // → silent (no push, but still authorized and visible in the inbox).
    //
    // Supersedes approverType + approverTeamId + approverUserId, which are still
    // READ for back-compat — see `resolveApproverList`, the single place that
    // reconciles the shapes. Those fields must stay in this schema: persisted
    // step configs are re-validated against it (the test-step RPC and
    // TestSuiteExecutionService), and in-flight Temporal runs replay the config
    // recorded by the `loadWorkflowDefinition` activity.
    approverList: z
      .array(
        z.object({
          type: z
            .enum(["team", "user"])
            .describe(
              "Whether this entry names an organizational team (um_user_groups) or a single user.",
            ),
          id: z
            .string()
            .uuid()
            .describe(
              "The team id when type is 'team', else the user id. Resolved to recipients at runtime, with out-of-office substitution either way.",
            ),
          notify: z
            .enum(["email", "in_app", "none"])
            .default("in_app")
            .describe(
              "Notification mode for this approver: 'email' (email + in-app), 'in_app' (in-app only), or 'none' (silent — still authorized, no push).",
            ),
        }),
      )
      .optional()
      .describe(
        "Approvers for this gate, OR-joined (any entry may decide). Teams and users may be mixed; each entry has its own notification mode. Supersedes approverType/approverTeamId/approverUserId.",
      ),
    // --- Superseded by approverList. Read-only back-compat; do not write. ---
    approverType: z
      .enum(["user", "team"])
      .default("user")
      .describe(
        "SUPERSEDED by approverList. Whether the approver is a single user (approverUserId) or an organizational team (approverTeamId). Defaults to 'user'.",
      ),
    approverTeamId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "SUPERSEDED by approverList. The single approver team (um_user_groups), read when approverList is empty and approverType is 'team'.",
      ),
    timeoutSeconds: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        "If set, the step auto-completes with decision 'timed_out' when no decision is made within this many seconds, letting the canvas route to an escalation path.",
      ),
    reminderIntervalHours: z
      .number()
      .int()
      .min(1)
      .max(720)
      .optional()
      .describe(
        "If set, re-sends the approval email and in-app notification to the same approvers every this many hours until a decision is recorded (no cap). Applies to pending event_driven gates on a draft sales order.",
      ),
    instructions: z
      .string()
      .default("")
      .describe("Instructions or context displayed to the approver"),
    notifyApprovers: z
      .boolean()
      .default(false)
      .describe(
        "When true, send an email notification to approvers when the step enters review state",
      ),
    senderMailbox: z
      .string()
      .optional()
      .describe(
        "The mailbox UPN (e.g. 'orders@company.com') to send notifications from via Microsoft Graph. Required when notifyApprovers is true.",
      ),
    emailSubject: z
      .string()
      .optional()
      .describe(
        "Custom email subject line. Defaults to 'Approval requested — {workflow name}'",
      ),
    // Reference to the email template used for the approver-notification body.
    // Either a built-in (`builtin:<key>`) or an org email-template lineage id
    // (uuid). Relaxed from .uuid() to carry the `builtin:` prefix. When unset, the
    // step renders the legacy built-in approval-request body (back-compatible).
    emailTemplateId: z
      .string()
      .optional()
      .describe(
        "Email template for the approver notification body — a built-in (`builtin:<key>`) or an org email-template id. Unset = built-in approval-request default.",
      ),
    approverUserId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "SUPERSEDED by approverList. Single-user approver, read when approverList is empty and approverType is 'user'. Resolved to their current email at runtime.",
      ),
  })
  .superRefine((cfg, ctx) => {
    // A "team" gate must name at least one team. Without one,
    // `resolveApproverList` yields nothing, so the gate raises with nobody
    // notified or authorized (only org admins) and, in event_driven mode, sits
    // pending forever. Reject it at config-validation time instead.
    //
    // Only legacy "team" gates are constrained, and only when `approverList` is
    // absent — an `approverList` gate is self-describing, and a "user" gate may
    // draw its approvers from the wired `approverEmails` input socket, so
    // neither carries a config-level requirement.
    const hasApproverList =
      Array.isArray(cfg.approverList) && cfg.approverList.length > 0;
    const hasLegacyTeam =
      typeof cfg.approverTeamId === "string" && cfg.approverTeamId.length > 0;
    if (!hasApproverList && cfg.approverType === "team" && !hasLegacyTeam) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A team approval gate needs at least one approver team.",
        path: ["approverList"],
      });
    }
    // Reminders are only ever sent for pending event_driven gates on a draft
    // sales order (the reminder sweep scans those). On a blocking gate or any
    // other subject the interval would validate but never fire — reject it so
    // the canvas doesn't promise reminders it cannot deliver. `executionMode`
    // defaults to "blocking", so an unset mode is rejected too.
    if (
      cfg.reminderIntervalHours !== undefined &&
      (cfg.inputType !== "sales_order" || cfg.executionMode !== "event_driven")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Approver reminders are only supported on event-driven Sales Order approval gates.",
        path: ["reminderIntervalHours"],
      });
    }
  });

const emits = [] as const;

export const approvalStep = defineStepConfig({
  type: "approval",
  name: "Approval",
  description: "Pause workflow for human approval before continuing",
  category: "Control Flow",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});
